import { useState, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Turnstile } from '@marsidev/react-turnstile'
import { signIn } from '../lib/supabase'
import { useToast } from '../components/Toast'
import SEO from '../components/SEO'
import Logo from '../components/Logo'

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY

export default function LoginPage() {
  const [email, setEmail]     = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  // Required because Supabase CAPTCHA protection is enabled at the
  // project level -- signInWithPassword without a token returns
  // "captcha protection: request disallowed". See SignUp.js for the
  // same pattern.
  const [captchaToken, setCaptchaToken] = useState('')
  const captchaRef = useRef(null)
  const navigate = useNavigate()
  const toast    = useToast()

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!captchaToken) { toast('Please wait a moment for the security check to complete', 'error'); return }
    setLoading(true)
    const { error } = await signIn(email, password, captchaToken)
    setLoading(false)
    if (error) {
      // Turnstile tokens are single-use. Reset for the next attempt.
      captchaRef.current?.reset()
      setCaptchaToken('')
      toast(error.message || 'Sign in failed', 'error')
    } else {
      navigate('/dashboard')
    }
  }

  return (
    <div style={{
      minHeight: '100vh', background: 'var(--surface)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
    }}>
      {/* Sign-in pages exist for existing users only and have no business
          ranking in search results. noindex prevents Google indexing form
          field labels as the page description. */}
      <SEO title="Sign in" noindex />
      <div style={{ position: 'fixed', top: 0, right: 0, width: '40vw', height: '40vw', background: 'radial-gradient(circle, rgba(72,146,145,0.06) 0%, transparent 70%)', pointerEvents: 'none' }} />

      <div className="fade-up" style={{ width: '100%', maxWidth: 440 }}>
        {/* Logo: the full PNG already contains the RECEIPT CHASER wordmark
            so we don't render a separate h1 underneath. */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <Logo variant="full" size={80} style={{ margin: '0 auto 20px' }} />
          <p style={{ color: 'var(--ink-muted)', fontSize: '0.9rem' }}>
            Sign in to your bookkeeper account
          </p>
        </div>

        {/* Card */}
        <div className="card" style={{ padding: 32 }}>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="email">Email address</label>
              <input
                id="email"
                type="email"
                className="input"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoFocus
              />
            </div>
            <div className="form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                <label htmlFor="password" style={{ margin: 0 }}>Password</label>
                <Link to="/forgot-password" style={{ fontSize: '0.8rem', color: 'var(--accent)' }}>
                  Forgot password?
                </Link>
              </div>
              <input
                id="password"
                type="password"
                className="input"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>
            <button
              type="submit"
              className="btn btn-primary btn-lg"
              disabled={loading || !captchaToken}
              style={{ width: '100%', justifyContent: 'center', marginTop: 8 }}
            >
              {loading ? 'Signing in…' : 'Sign in →'}
            </button>
            {TURNSTILE_SITE_KEY ? (
              <div style={{ marginTop: 16 }}>
                <Turnstile
                  ref={captchaRef}
                  siteKey={TURNSTILE_SITE_KEY}
                  onSuccess={token => setCaptchaToken(token)}
                  onError={() => setCaptchaToken('')}
                  onExpire={() => setCaptchaToken('')}
                  options={{ theme: 'light', size: 'flexible' }}
                />
              </div>
            ) : null}
          </form>
        </div>

        <p style={{ textAlign: 'center', marginTop: 20, fontSize: '0.88rem', color: 'var(--ink-muted)' }}>
          New to Receipt Chaser?{' '}
          <Link to="/signup" style={{ color: 'var(--accent)', fontWeight: 500 }}>
            Create a free account →
          </Link>
        </p>
      </div>
    </div>
  )
}
